import { Decoration, EditorView } from '@codemirror/view';
import { StateEffect, StateField } from '@codemirror/state';

// Define highlight effects
const removeHighlightEffect = StateEffect.define();
const addHighlightEffect = StateEffect.define();

// Styling for highlights
const searchStyles = EditorView.baseTheme({
    ".cm-search-highlight": {
        backgroundColor: "var(--color-search-highlight-bg)",
        borderRadius: "2px",
        padding: "1px 0",
        outline: "1px solid var(--color-search-highlight-border)",
        display: "inline"
    },
    ".cm-search-highlight.cm-current": {
        backgroundColor: "var(--color-search-highlight-current-bg)",
        outline: "2px solid var(--color-search-highlight-current-border)",
        boxShadow: "0 0 4px var(--color-search-highlight-current-shadow)",
        zIndex: "2",
        display: "inline"
    }
});

// Create a state field for our highlights
const highlightField = StateField.define({
    create() {
        return Decoration.none;
    },
    
    update(highlights, tr) {
        // Map the decorations to changes
        highlights = highlights.map(tr.changes);
        
        // Apply the effects
        for (let e of tr.effects) {
            if (e.is(removeHighlightEffect)) {
                highlights = Decoration.none;
            } else if (e.is(addHighlightEffect)) {
                highlights = e.value;
            }
        }
        
        return highlights;
    },
    
    provide: field => EditorView.decorations.from(field)
});

// Function to clear search results
function clearSearchResults(view) {
    if (!view || !view.state || !view.dispatch) {
        return;
    }
    
    try {
        view.dispatch({
            effects: [removeHighlightEffect.of(null)]
        });
    } catch (error) {
        console.error("Error clearing search results:", error);
    }
}

// Function to set search results with highlights
function setSearchResults(view, results = [], currentIndex = -1, _options = {}) {
    if (!view || !view.state || !view.dispatch) {
        return;
    }
    
    // Clear existing highlights first
    clearSearchResults(view);
    
    // If no results, we're done
    if (!results.length) {
        return;
    }
    
    try {
        // Create decorations for all results
        const decorations = [];
        
        results.forEach((result, index) => {
            const isCurrent = index === currentIndex;
            const className = isCurrent ? 'cm-search-highlight cm-current' : 'cm-search-highlight';
            
            decorations.push(
                Decoration.mark({
                    class: className
                }).range(result.from, result.to)
            );
        });
        
        // Apply decorations
        const decorationSet = Decoration.set(decorations.sort((a, b) => a.from - b.from));
        
        view.dispatch({
            effects: [addHighlightEffect.of(decorationSet)]
        });
    } catch (error) {
        console.error("Error setting search results:", error);
    }
}

// Export the search highlighter with minimal interface
export const SearchHighlighterCodeMirror = {
    extension: [
        highlightField,
        searchStyles
    ],
    setSearchResults,
    clearSearchResults
};